import { DeckMap } from "@/components/deck-map/deck-map";
import type { MapThemePalette } from "@/components/deck-map/_helpers/map-theme";
import { createRouteMapLayers } from "@/components/deck-map/_helpers/create-route-map-layers";
import { createMapRoute, createMapRouteData } from "@/components/deck-map/_helpers/map-route";
import { getRouteViewState, type LngLat, type ViewportSize } from "@/components/deck-map/_helpers/route-arc";
import { useRouteAnimationFrame } from "@/components/deck-map/_hooks/use-route-animation-frame";
import { TextLayer } from "deck.gl";
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface RouteMapProps {
  originCoords: LngLat;
  destinationCoords: LngLat;
  currentCoords: LngLat | null;
  palette: MapThemePalette;
}

interface RouteMapLabel {
  position: LngLat;
  text: string;
  pixelOffset: [number, number];
  textAnchor: "start" | "middle";
}

export function RouteMap({ originCoords, destinationCoords, currentCoords, palette }: RouteMapProps) {
  const { t } = useTranslation("fit");
  const containerRef = useRef<HTMLDivElement>(null);
  const [viewportSize, setViewportSize] = useState<ViewportSize | null>(null);
  const animationFrame = useRouteAnimationFrame();
  const [originLongitude, originLatitude] = originCoords;
  const [destinationLongitude, destinationLatitude] = destinationCoords;
  const currentLongitude = currentCoords?.[0];
  const currentLatitude = currentCoords?.[1];

  const route = useMemo(
    () =>
      createMapRoute(
        [originLongitude, originLatitude],
        [destinationLongitude, destinationLatitude],
        currentLongitude != null && currentLatitude != null ? [currentLongitude, currentLatitude] : null,
      ),
    [originLongitude, originLatitude, destinationLongitude, destinationLatitude, currentLongitude, currentLatitude],
  );
  const routeData = useMemo(() => createMapRouteData([route]), [route]);
  const viewState = useMemo(
    () => (viewportSize ? getRouteViewState(route.origin, route.destination, viewportSize, route.current) : null),
    [route, viewportSize],
  );
  const labels = useMemo(() => {
    const waypointLabels: RouteMapLabel[] = [
      { position: route.origin, text: t("From"), pixelOffset: [0, 26], textAnchor: "middle" },
      { position: route.destination, text: t("To"), pixelOffset: [18, 0], textAnchor: "start" },
    ];

    if (route.current) {
      waypointLabels.push({
        position: route.current,
        text: t("CurrentBranch"),
        pixelOffset: [0, -26],
        textAnchor: "middle",
      });
    }

    return waypointLabels;
  }, [route, t]);

  useLayoutEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      const { clientWidth: width, clientHeight: height } = container;

      if (width === 0 || height === 0) {
        return;
      }

      setViewportSize((currentSize) =>
        currentSize?.width === width && currentSize?.height === height ? currentSize : { width, height },
      );
    });

    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, []);

  const layers = [
    createRouteMapLayers({
      data: routeData,
      animationFrame,
      palette,
    }),
    new TextLayer<RouteMapLabel>({
      id: "route-waypoint-labels",
      data: labels,
      getPosition: (label) => label.position,
      getText: (label) => label.text,
      getSize: 12,
      getColor: [...palette.route.headRgb, 255],
      getPixelOffset: (label) => label.pixelOffset,
      getTextAnchor: (label) => label.textAnchor,
      getAlignmentBaseline: "center",
      background: true,
      getBackgroundColor: palette.mode === "dark" ? [20, 24, 28, 220] : [255, 255, 255, 230],
      backgroundPadding: [5, 3],
      billboard: true,
      parameters: { depthWriteEnabled: false },
    }),
  ];

  return (
    <div
      ref={containerRef}
      className="relative aspect-video w-full overflow-hidden rounded-lg border"
      style={{
        backgroundColor: palette.surface.mapBackground,
        borderColor: palette.surface.border,
      }}
    >
      {viewState && (
        <DeckMap
          mapStyle={palette.basemapStyleUrl}
          initialViewState={viewState}
          controller
          layers={layers}
          getTooltip={() => null}
        />
      )}
    </div>
  );
}
